import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Keyboard,
  Pressable,
  Platform,
  ScrollView,
  ActivityIndicator,
} from "react-native";
import Colors from "../../../constants/colors";
import ShieldIcon from "../../../../../../assets/images/Shield.svg";
import LocationB from "../../../../../../assets/images/LocationCash.svg";
import { ComponentStyles } from "../../../constants/componentStyles";
import FloatingTextInputComponent, {
  personNameFloatingKeyboardProps,
} from "./FloatingTextInputComponent";
import FloatingDropdownComponent from "./FloatingDropdownComponent";
import {
  isPersonNameSegmentValid,
  sanitizeCityInput,
} from "../../../helper/personNameValidation";
import {
  ADDRESS_LINE_MAX_LENGTH,
  CITY_FIELD_MAX_LENGTH,
  hasRepeatingAddressSpecialCharacters,
  isAddressOrPostalContentValid,
  isTwoLetterStateCode,
  POSTAL_CODE_MAX_LENGTH,
  sanitizeAddressLineInput,
  sanitizePostalCodeInput,
  toTwoLetterStateCode,
  US_STATE_FIELD_MAX_LENGTH,
} from "../../../helper/addressFieldValidation";
import { getPlaceDetails } from "../../../services/location.service";
import { loadCountries } from "../../../services/common.service";
import { parsePlaceDetailsForSenderAddress } from "../../../helper/placeDetailsHelper";
import {
  useAwsLocationAddressSearch,
  type AwsLocationSuggestion,
} from "../../../hooks/useAwsLocationAddressSearch";
import { ADDRESS_AUTOCOMPLETE_DROPDOWN_Z_INDEX } from "../../../constants/addressAutocompleteLayers";

type ApiCountry = {
  CountryCode: string;
  CountryName: string;
  IsActive?: number | boolean;
};

/** Split AWS Place suggestion `Text` (comma-separated) into street vs city/state/postal/country. */
function splitAddressSuggestionText(text: string): {
  primary: string;
  secondary: string;
} {
  const trimmed = (text || "").trim();
  if (!trimmed) {
    return { primary: "", secondary: "" };
  }
  const parts = trimmed
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length <= 1) {
    return { primary: trimmed, secondary: "" };
  }
  return {
    primary: parts[0] ?? "",
    secondary: parts.slice(1).join(", "),
  };
}

export type AddressFieldBindings = {
  address: string;
  city: string;
  zipCode: string;
  state: string;
  /** ISO3 country code; only used when `collectCountry` is true. */
  country?: string;
  setAddress: (value: string) => void;
  setCity: (value: string) => void;
  setZipCode: (value: string) => void;
  setState: (value: string) => void;
  setCountry?: (value: string) => void;
};

export type AddressFieldLabels = {
  line1: string;
  line2: string;
  city: string;
  postal: string;
  state: string;
  country?: string;
  search?: string;
};

export type AddressFieldTestIds = {
  search: string;
  suggestion: string;
  enterManually: string;
  searchInstead: string;
  address: string;
  address2: string;
  city: string;
  zip: string;
  state: string;
  stateOption: string;
  country?: string;
  countryOption?: string;
  continue: string;
};

type Props = {
  bindings: AddressFieldBindings;
  labels: AddressFieldLabels;
  testIds: AddressFieldTestIds;
  onValidationChange?: (isValid: boolean) => void;
  onContinue?: () => void;
  testID?: string;
  title?: string;
  subtitle?: string;
  /**
   * When true, show and require Country (same idea as qs-web `collectCountry`).
   * Off by default so receive / bill-pay address steps stay unchanged.
   */
  collectCountry?: boolean;
};

export interface AddressSearchManualStepHandle {
  submit: () => Promise<boolean>;
}

type AddressEntryMode = "search" | "manual";

const AddressSearchManualStep = forwardRef<AddressSearchManualStepHandle, Props>(
  (
    {
      bindings,
      labels,
      testIds,
      onValidationChange,
      onContinue,
      testID,
      title,
      subtitle,
      collectCountry = false,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const {
      address,
      city,
      zipCode,
      state,
      country = "",
      setAddress,
      setCity,
      setZipCode,
      setState,
      setCountry,
    } = bindings;

    const [errors, setErrors] = useState({
      address: "",
      address2: "",
      city: "",
      zipCode: "",
      state: "",
      country: "",
    });
    const [countryOptions, setCountryOptions] = useState<
      Array<{ value: string; label: string }>
    >([]);
    const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
    const [availableStates, setAvailableStates] = useState<
      Array<{ stateid: number; stateAbbrivation: string; stateName: string }>
    >([]);
    const [stateDropdownOpen, setStateDropdownOpen] = useState(false);

    const countryCode = country.trim().toUpperCase();
    const showStateDropdown =
      collectCountry && (countryCode === "USA" || countryCode === "CAN");

    const hasStoredAddressFields = Boolean(
      address.trim() ||
        city.trim() ||
        zipCode.trim() ||
        state.trim() ||
        (collectCountry && country.trim())
    );

    const [entryMode, setEntryMode] = useState<AddressEntryMode>(() =>
      hasStoredAddressFields ? "manual" : "search"
    );
    const [searchQuery, setSearchQuery] = useState(() =>
      hasStoredAddressFields ? address : ""
    );
    const [addressLine2, setAddressLine2] = useState("");

    const {
      suggestions,
      showSuggestions,
      setShowSuggestions,
      loading: searchLoading,
      search,
      clearSuggestions,
      cancelDismissSuggestions,
      scheduleDismissSuggestions,
    } = useAwsLocationAddressSearch();

    const searchPlaceholder =
      labels.search ||
      t("COUNTRY_STEP_SEARCH_PLACEHOLDER");

    const line2Valid =
      !addressLine2.trim() ||
      isAddressOrPostalContentValid(addressLine2, ADDRESS_LINE_MAX_LENGTH);

    useEffect(() => {
      if (!collectCountry) {
        setCountryOptions([]);
        return;
      }
      let cancelled = false;
      const fetchCountries = async () => {
        try {
          const result = await loadCountries();
          const apiCountries: ApiCountry[] = (result?.data ?? []).filter(
            (c: ApiCountry) => {
              const isActive =
                typeof c.IsActive === "number" ? c.IsActive === 1 : !!c.IsActive;
              return isActive && !!c.CountryName && !!c.CountryCode;
            }
          );
          if (cancelled) return;
          setCountryOptions(
            apiCountries.map((c) => ({
              value: String(c.CountryCode).trim().toUpperCase(),
              label: c.CountryName,
            }))
          );
        } catch (e) {
          console.error("Failed to load countries for address step", e);
          if (!cancelled) setCountryOptions([]);
        }
      };
      void fetchCountries();
      return () => {
        cancelled = true;
      };
    }, [collectCountry]);

    useEffect(() => {
      if (!showStateDropdown) {
        setAvailableStates([]);
        return;
      }
      let cancelled = false;
      const loadStates = async () => {
        try {
          const result = await loadCountries();
          const list: ApiCountry[] = (result?.data ?? []) as ApiCountry[];
          const countryObj = list.find((c) => c?.CountryCode === countryCode);
          const states: Array<{
            stateid: number;
            stateAbbrivation: string;
            stateName: string;
          }> = Array.isArray((countryObj as any)?.States)
            ? (countryObj as any).States
            : [];
          if (!cancelled) setAvailableStates(states);
        } catch (e) {
          console.error("Failed to load states for address step", countryCode, e);
          if (!cancelled) setAvailableStates([]);
        }
      };
      void loadStates();
      return () => {
        cancelled = true;
      };
    }, [showStateDropdown, countryCode]);

    const countryValid = !collectCountry || country.trim().length > 0;
    // USA/CAN: state required (2-letter abbr). Other countries: optional, filled from search when present. We don't want to allow empty state for other countries.
    const stateValid = showStateDropdown
      ? isTwoLetterStateCode(state)
      : !state.trim() || isTwoLetterStateCode(state);

    const isValid =
      address.trim().length > 0 &&
      isAddressOrPostalContentValid(address, ADDRESS_LINE_MAX_LENGTH) &&
      line2Valid &&
      isPersonNameSegmentValid(city, true, CITY_FIELD_MAX_LENGTH) &&
      zipCode.trim().length > 0 &&
      isAddressOrPostalContentValid(zipCode, POSTAL_CODE_MAX_LENGTH) &&
      stateValid &&
      countryValid;

    // Drop non-abbreviation values (e.g. full region name left in store from older builds).
    useEffect(() => {
      if (state.trim() && !isTwoLetterStateCode(state)) {
        setState("");
      }
    }, [state, setState]);

    useEffect(() => {
      if (isValid) {
        setErrors({
          address: "",
          address2: "",
          city: "",
          zipCode: "",
          state: "",
          country: "",
        });
      }
    }, [isValid]);

    useEffect(() => {
      onValidationChange?.(isValid);
    }, [isValid, onValidationChange]);

    useImperativeHandle(ref, () => ({
      submit: async () => {
        const newErrors = {
          address: !address.trim()
            ? t("ADDRESS_REQUIRED")
            : !isAddressOrPostalContentValid(address, ADDRESS_LINE_MAX_LENGTH)
              ? address.length > ADDRESS_LINE_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", {
                    count: ADDRESS_LINE_MAX_LENGTH,
                  })
                : hasRepeatingAddressSpecialCharacters(address)
                  ? t("ADDRESS_NO_REPEATING_SPECIAL_CHARS")
                  : t("ADDRESS_POSTAL_INVALID_CHARS")
              : "",
          address2:
            addressLine2.trim() &&
            !isAddressOrPostalContentValid(addressLine2, ADDRESS_LINE_MAX_LENGTH)
              ? addressLine2.length > ADDRESS_LINE_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", {
                    count: ADDRESS_LINE_MAX_LENGTH,
                  })
                : hasRepeatingAddressSpecialCharacters(addressLine2)
                  ? t("ADDRESS_NO_REPEATING_SPECIAL_CHARS")
                  : t("ADDRESS_POSTAL_INVALID_CHARS")
              : "",
          city: !city.trim()
            ? t("CITY_REQUIRED")
            : !isPersonNameSegmentValid(city, true, CITY_FIELD_MAX_LENGTH)
              ? city.length > CITY_FIELD_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: CITY_FIELD_MAX_LENGTH })
                : t("PERSON_NAME_ERROR_LETTERS_ONLY")
              : "",
          zipCode: !zipCode.trim()
            ? t("POSTAL_CODE_REQUIRED")
            : !isAddressOrPostalContentValid(zipCode, POSTAL_CODE_MAX_LENGTH)
              ? zipCode.length > POSTAL_CODE_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: POSTAL_CODE_MAX_LENGTH })
                : hasRepeatingAddressSpecialCharacters(zipCode)
                  ? t("ADDRESS_NO_REPEATING_SPECIAL_CHARS")
                  : t("ADDRESS_POSTAL_INVALID_CHARS")
              : "",
          state:
            showStateDropdown && !state.trim()
              ? t("STATE_REQUIRED")
              : state.trim() && !isTwoLetterStateCode(state)
                ? t("STATE_INVALID", { example: "MN" })
                : "",
          country:
            collectCountry && !country.trim()
              ? t("COUNTRY_REQUIRED")
              : "",
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
      },
    }));

    const handleSearchQueryChange = (text: string) => {
      const s = sanitizeAddressLineInput(text);
      setSearchQuery(s);
      if (errors.address) {
        setErrors((prev) => ({ ...prev, address: "" }));
      }
      search(s);
    };

    const handleSelectSuggestion = async (item: AwsLocationSuggestion) => {
      cancelDismissSuggestions();
      try {
        const place = await getPlaceDetails(item?.PlaceId);
        const parsed = parsePlaceDetailsForSenderAddress(place, {
          suggestionText: String(item?.Text ?? ""),
        });
        setAddress(parsed.line1);
        setAddressLine2(parsed.line2);
        setCity(parsed.city);
        setZipCode(parsed.zip);
        setState(toTwoLetterStateCode(parsed.state));
        if (collectCountry && parsed.country && setCountry) {
          setCountry(parsed.country);
        }
        setSearchQuery(parsed.line1 || String(item?.Text ?? ""));
      } catch (e) {
        console.error("GetPlace error", e);
      }
      setShowSuggestions(false);
      setEntryMode("manual");
      Keyboard.dismiss();
    };

    const openManualEntry = () => {
      cancelDismissSuggestions();
      setShowSuggestions(false);
      setEntryMode("manual");
      if (searchQuery.trim() && !address.trim()) {
        setAddress(sanitizeAddressLineInput(searchQuery));
      }
    };

    const openSearchEntry = () => {
      cancelDismissSuggestions();
      clearSuggestions();
      setEntryMode("search");
      if (address.trim()) {
        setSearchQuery(address);
      }
    };

    const renderSuggestionsList = () => {
      if (!showSuggestions || suggestions.length === 0) {
        return null;
      }
      return (
        <View style={styles.dropdownContainer}>
          <FlatList
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            data={suggestions}
            keyExtractor={(item) => String(item?.PlaceId)}
            renderItem={({ item }) => {
              const { primary, secondary } = splitAddressSuggestionText(item.Text);
              return (
                <TouchableOpacity
                  testID={testIds.suggestion}
                  style={styles.suggestionItem}
                  onPressIn={cancelDismissSuggestions}
                  onPress={() => handleSelectSuggestion(item)}
                >
                  <View style={styles.suggestionIconWrap}>
                    <LocationB width={16} height={16} />
                  </View>
                  <Text style={styles.suggestionInlineWrap}>
                    <Text style={styles.suggestionPrimaryText}>{primary}</Text>
                    {secondary ? (
                      <Text style={styles.suggestionSecondaryInline}>
                        {` ${secondary}`}
                      </Text>
                    ) : null}
                  </Text>
                </TouchableOpacity>
              );
            }}
          />
        </View>
      );
    };

    const renderManualAddressForm = () => (
      <View style={styles.manualForm}>
        <FloatingTextInputComponent
          testID={testIds.address}
          label={labels.line1}
          value={address}
          isAutoFocus={entryMode === "manual"}
          maxLength={ADDRESS_LINE_MAX_LENGTH}
          onChangeText={(val) => {
            setAddress(sanitizeAddressLineInput(val));
            if (errors.address) {
              setErrors((prev) => ({ ...prev, address: "" }));
            }
          }}
          errorText={errors.address}
          onClearError={() => setErrors((prev) => ({ ...prev, address: "" }))}
          autoComplete="street-address"
        />
        <FloatingTextInputComponent
          testID={testIds.address2}
          label={labels.line2}
          value={addressLine2}
          maxLength={ADDRESS_LINE_MAX_LENGTH}
          onChangeText={(val) => {
            setAddressLine2(sanitizeAddressLineInput(val));
            if (errors.address2) {
              setErrors((prev) => ({ ...prev, address2: "" }));
            }
          }}
          errorText={errors.address2}
          onClearError={() => setErrors((prev) => ({ ...prev, address2: "" }))}
          autoComplete="address-line2"
        />
        <FloatingTextInputComponent
          {...personNameFloatingKeyboardProps}
          testID={testIds.city}
          label={labels.city}
          value={city}
          maxLength={CITY_FIELD_MAX_LENGTH}
          onChangeText={(val) => setCity(sanitizeCityInput(val, CITY_FIELD_MAX_LENGTH))}
          errorText={errors.city}
          onClearError={() => setErrors((prev) => ({ ...prev, city: "" }))}
          autoComplete="postal-address-locality"
        />
        <FloatingTextInputComponent
          testID={testIds.zip}
          label={labels.postal}
          value={zipCode}
          maxLength={POSTAL_CODE_MAX_LENGTH}
          onChangeText={(val) => setZipCode(sanitizePostalCodeInput(val))}
          errorText={errors.zipCode}
          onClearError={() => setErrors((prev) => ({ ...prev, zipCode: "" }))}
          autoComplete="postal-code"
        />
        {collectCountry ? (
          <FloatingDropdownComponent
            testID={testIds.country || "senderCountry"}
            optionTestID={testIds.countryOption || "senderCountryOption"}
            label={labels.country || t("SELECT_COUNTRY")}
            value={country}
            options={countryOptions}
            isOpen={countryDropdownOpen}
            onOpenChange={setCountryDropdownOpen}
            onSelect={(value) => {
              setCountry?.(value);
              setState("");
              setStateDropdownOpen(false);
              setErrors((prev) => ({ ...prev, country: "", state: "" }));
            }}
            errorText={errors.country}
            onClearError={() => setErrors((prev) => ({ ...prev, country: "" }))}
          />
        ) : null}
        {showStateDropdown ? (
          <FloatingDropdownComponent
            testID={testIds.state}
            optionTestID={testIds.stateOption}
            label={labels.state}
            value={state}
            displayValue={
              availableStates.find(
                (s) => String(s.stateAbbrivation) === state
              )?.stateName
            }
            options={availableStates.map((opt) => ({
              value: String(opt.stateAbbrivation),
              label: opt.stateName,
            }))}
            isOpen={stateDropdownOpen}
            onOpenChange={setStateDropdownOpen}
            onSelect={(selected) => {
              setState(selected);
              setErrors((prev) => ({ ...prev, state: "" }));
            }}
            errorText={errors.state}
            onClearError={() => setErrors((prev) => ({ ...prev, state: "" }))}
          />
        ) : (
          <FloatingTextInputComponent
            {...personNameFloatingKeyboardProps}
            testID={testIds.state}
            label={labels.state}
            value={state}
            maxLength={US_STATE_FIELD_MAX_LENGTH}
            editable={false}
            onChangeText={() => {
              /* filled from address search only (2-letter abbr) */
            }}
            errorText={errors.state}
            onClearError={() => setErrors((prev) => ({ ...prev, state: "" }))}
            autoComplete="postal-address-region"
          />
        )}
      </View>
    );

    return (
      <Pressable
        style={styles.container}
        testID={testID}
        onPress={() => {
          if (showSuggestions) {
            cancelDismissSuggestions();
            setShowSuggestions(false);
            Keyboard.dismiss();
          }
        }}
      >
        <View style={styles.headerSection}>
          <Text style={styles.title}>{title ?? t("SENDER_ADDRESS_TITLE")}</Text>
          <Text style={styles.subtitle}>
            {subtitle ?? t("SENDER_ADDRESS_SUBTITLE")}
          </Text>
        </View>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          onScrollBeginDrag={() => {
            if (showSuggestions) {
              cancelDismissSuggestions();
              setShowSuggestions(false);
            }
          }}
        >
          <View style={styles.form}>
            {entryMode === "search" ? (
              <>
                <FloatingTextInputComponent
                  testID={testIds.search}
                  label={searchPlaceholder}
                  value={searchQuery}
                  isAutoFocus
                  maxLength={ADDRESS_LINE_MAX_LENGTH}
                  onChangeText={handleSearchQueryChange}
                  autoComplete="street-address"
                  onFocus={cancelDismissSuggestions}
                  onBlur={scheduleDismissSuggestions}
                />
                {searchLoading ? (
                  <View style={styles.searchLoadingRow}>
                    <ActivityIndicator size="small" color={Colors.titleTextColor} />
                    <Text style={styles.searchLoadingText}>
                      {t("SENDER_ADDRESS_SEARCHING") || "Searching..."}
                    </Text>
                  </View>
                ) : null}
                {renderSuggestionsList()}
                <TouchableOpacity
                  testID={testIds.enterManually}
                  style={styles.manualBtn}
                  onPress={openManualEntry}
                  accessibilityRole="button"
                >
                  <Text style={styles.manualText}>{t("SENDER_ADDRESS_MANUAL")}</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                {renderManualAddressForm()}
                <TouchableOpacity
                  testID={testIds.searchInstead}
                  style={styles.searchInsteadBtn}
                  onPress={openSearchEntry}
                  accessibilityRole="button"
                >
                  <Text style={styles.searchInsteadText}>
                    {t("SENDER_ADDRESS_SEARCH_INSTEAD")}
                  </Text>
                </TouchableOpacity>
                {/* {renderManualAddressForm()} */}
              </>
            )}
          </View>

          <View style={styles.hintBox}>
            <ShieldIcon width={24} height={24} />
            <Text style={styles.hintText}>{t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}</Text>
          </View>
          <View style={styles.scrollSpacer} />
          {onContinue ? (
            <TouchableOpacity
              testID={testIds.continue}
              style={styles.continueButton}
              onPress={() => onContinue()}
            >
              <Text style={styles.continueButtonText}>{t("STRKEY_CONTINUE")}</Text>
            </TouchableOpacity>
          ) : null}
        </ScrollView>
      </Pressable>
    );
  }
);

AddressSearchManualStep.displayName = "AddressSearchManualStep";

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
    overflow: "visible",
  },
  headerSection: {
    flexShrink: 0,
    width: "100%",
  },
  scroll: {
    flex: 1,
    width: "100%",
    ...(Platform.OS === "web" && { minHeight: 0 as any, overflow: "visible" as any }),
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
  },
  scrollSpacer: {
    flexGrow: 1,
  },
  continueButton: {
    backgroundColor: Colors.black,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  continueButtonText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "600",
    color: Colors.white,
    lineHeight: 20,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24,
  },
  form: {
    overflow: "visible",
    zIndex: 999,
    elevation: 20,
  },
  manualForm: {
    overflow: "visible",
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  suggestionIconWrap: {
    marginRight: 12,
    flexShrink: 0,
    justifyContent: "center",
  },
  suggestionInlineWrap: {
    flex: 1,
    minWidth: 0,
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    lineHeight: 18,
  },
  suggestionTextCol: {
    flex: 1,
    minWidth: 0,
  },
  suggestionPrimaryText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "400",
    color: Colors.textPrimary,
    marginRight: 8
  },
  suggestionSecondaryText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "400",
    color: Colors.infoSubValue,
    marginTop: 2,
  },
  suggestionSecondaryInline: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "400",
    color: Colors.infoSubValue,
  },
  dropdownContainer: {
    // maxHeight: 240,
    borderRadius: 8,
    backgroundColor: Colors.white,
    marginTop: 4,
    position: "relative",
    zIndex: ADDRESS_AUTOCOMPLETE_DROPDOWN_Z_INDEX,
    elevation: 30,
  },
  manualBtn: {
    paddingTop: 16,
    paddingBottom: 24,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    // paddingVertical: 12,
  },
  manualText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 16,
    fontWeight: "500",
    color: Colors.black,
    lineHeight: 24,
  },
  searchInsteadBtn: {
    marginTop: 16,
    marginBottom: 24,
    alignItems: "center",
  },
  searchInsteadText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 16,
    fontWeight: "500",
    lineHeight: 24,
    color: Colors.black
  },
  searchLoadingRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
  searchLoadingText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    color: Colors.subTitleColor,
    marginLeft: 8,
  },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 10,
    padding: 16,
    // marginTop: 32,
    backgroundColor: Colors.white,
    marginBottom: 24,
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: "400",
    color: Colors.darkGrey,
    lineHeight: 18,
    paddingLeft: 16,
  },
});

export default AddressSearchManualStep;
